import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiUsers, FiTrendingUp, FiAward, FiCalendar, FiArrowRight } =
  FiIcons;

const About = () => {
  const { content } = useCMS();
  const { company, vision } = content;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const milestones = [
    {
      year: '2016',
      title: 'Fondation',
      description:
        'Création de QHSE Consulting à Douala avec une vision claire d\'excellence',
    },
    {
      year: '2018',
      title: 'Agréments officiels',
      description: 'Obtention des agréments du Ministère de la Santé Publique',
    },
    {
      year: '2020',
      title: 'Expansion régionale',
      description: 'Extension des services dans la sous-région CEMAC',
    },
    {
      year: '2024',
      title: 'Leader reconnu',
      description: 'Plus de 200 clients accompagnés vers la certification ISO',
    },
  ];

  const stats = [
    { icon: FiUsers, value: '200+', label: 'Clients satisfaits' },
    { icon: FiAward, value: '150+', label: 'Certifications ISO' },
    { icon: FiTrendingUp, value: '8+', label: 'Années d\'expérience' },
    { icon: FiTarget, value: '100%', label: 'Taux de réussite' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-pink-600 via-blue-600 to-green-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              À propos de QHSE Consulting
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl mx-auto">
              Depuis 2016, nous accompagnons les entreprises camerounaises et de
              la sous-région CEMAC vers l'excellence en Qualité, Hygiène,
              Sécurité et Environnement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants}>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-pink-600 to-blue-600 bg-clip-text text-transparent">
                Notre Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {company.mission}
              </p>
              <p className="text-gray-600 leading-relaxed">
                Fondée en septembre 2016, QHSE Consulting s'est imposée comme un
                partenaire de confiance pour les entreprises souhaitant
                améliorer leur performance opérationnelle et leur conformité
                réglementaire.
              </p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-br from-pink-50 to-blue-50 rounded-2xl p-8 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Notre Vision
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {vision.vision}
              </p>
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">
                  Nos Engagements
                </h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center space-x-2">
                    <span className="text-pink-600 font-bold">✓</span>
                    <span>Excellence technique et méthodologique</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-pink-600 font-bold">✓</span>
                    <span>Accompagnement personnalisé</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-pink-600 font-bold">✓</span>
                    <span>Respect des délais et budgets</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-pink-600 font-bold">✓</span>
                    <span>Formation continue de nos équipes</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Nos Valeurs
            </h2>
            <p className="text-xl text-gray-600">
              Les principes qui guident notre action quotidienne
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[...vision.values.fundamental.items, ...vision.values.professional.items].map(
              (value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-center group"
                  whileHover={{ y: -10 }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={FiTarget} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {value.name}
                  </h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              )
            )}
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
              Notre Parcours
            </h2>
            <p className="text-xl text-gray-600">
              8 années d'innovation et de croissance continue
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-0.5 w-0.5 h-full bg-pink-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${
                    index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                  }`}
                >
                  <div
                    className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}
                  >
                    <motion.div
                      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                      whileHover={{ y: -5 }}
                    >
                      <div className="text-3xl font-bold text-pink-600 mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </motion.div>
                  </div>
                  <div className="w-4 h-4 bg-pink-500 rounded-full border-4 border-white shadow-lg z-10"></div>
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-r from-pink-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Nos Résultats en Chiffres
            </h2>
            <p className="text-xl text-blue-100">
              La confiance de nos clients se mesure par nos réalisations
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
                whileHover={{ scale: 1.1 }}
              >
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-blue-100">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Rejoignez nos clients satisfaits
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Découvrez comment QHSE Consulting peut transformer votre approche
              de la qualité, de la sécurité et de l'environnement.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={itemVariants}>
                <Link
                  to="/contact"
                  className="bg-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-pink-700 transition-colors inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Demander une consultation
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                </Link>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Link
                  to="/services"
                  className="border-2 border-pink-600 text-pink-600 px-8 py-4 rounded-lg font-semibold hover:bg-pink-600 hover:text-white transition-colors inline-flex items-center justify-center"
                >
                  Découvrir nos services
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;